<template>
  <component
    :is="UiTemplate"
    :result="task.resultData"
    :content="content"
    :output-schema="taskConfigData?.output"
    :running="isRunning || loading || (task.queued === 1 && !hasError)"
    :has-error="hasError"
    :error-messages="errorMessages"
    :is-sub-task="isSubTask"
    :task-type="task.taskType"
    :sub-tasks-expanded="task.subTasksExpanded"
    :task-id="task.id"
    :use-quick-task-wrapper="useQuickTaskWrapper"
    hide-interaction
  />
</template>

<script setup lang="ts">
import type { IContentBase } from "@/types/content";
import type { ISubTask, ITaskBase } from "@/types/task";
const defaultComponent = defineAsyncComponent(() => import("./ResultText.vue"));
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const props = defineProps<{
  task: ITaskBase | ISubTask;
  content?: IContentBase[];
  isSubTask?: boolean;
  useQuickTaskWrapper?: boolean;
  loading?: boolean;
}>();

const { t } = withI18nHelpers();
const { getTaskByName } = newTaskRouter();
const taskConfigData = computed(() => getTaskByName(props.task.taskType));

const isRunning = computed(
  () =>
    props.task.running === 1 ||
    (!!props.task.subTasksExpanded &&
      props.task.subTasksExpanded.some(subTask => subTask.running === 1))
);

const hasError = computed(
  () =>
    !!props.task.error || props.task.subTasksExpanded?.some(subTask => !!subTask.error)
);

const errorMessages = computed(() => {
  const messages: Record<string, string> = {};
  if (!hasError.value) return messages;
  const parentError = props.task.failedMessage || "";
  if (parentError) messages[props.task.taskType] = parentError;
  if (!props.task.subTasksExpanded) return messages;
  props.task.subTasksExpanded.forEach(subTask => {
    if (subTask.error === 1) {
      const err = subTask.failedMessage || t("errors.taskFailed");
      messages[subTask.taskType] = err;
    }
  });
  return messages;
});

const UiTemplate = computed(() => {
  const output = taskConfigData.value?.output;
  // Check task-specific routing FIRST before checking output type
  if (taskConfigData.value) {
    const key = taskConfigData.value.namespace + "/" + taskConfigData.value.key;

    switch (key) {
      case "social-media/facebook/announce-event":
        return defineAsyncComponent(() => import("./TaskResultFacebookEvent.vue"));
      case "website/tools/review-webpage":
        return defineAsyncComponent(() => import("./TaskResultReviewWebpage.vue"));
      case "social-media/combined/write-all-social":
        return defineAsyncComponent(() => import("./TaskWriteAllSocialPosts.vue"));
      case "copywriting/article/research-write-post":
        return defineAsyncComponent(() => import("./TaskResultResearchWritePost.vue"));
      case "website/tools/generate-image":
      case "website/tools/generate-qr-code":
        return defineAsyncComponent(() => import("./TaskResultImage.vue"));
      case "copywriting/article/write-post":
        return defineAsyncComponent(() => import("./TaskResultWriteArticle.vue"));
      case "social-media/twitter/write-bio":
        return defineAsyncComponent(() => import("./meta/TwitterProfile.vue"));
      case "communications-outreach/email-marketing/email-series":
        return defineAsyncComponent(() => import("./TaskResultEmailCampaign.vue"));
      case "content/combined/write-posts":
        return defineAsyncComponent(
          () => import("./TaskResultContentCombineWritePost.vue")
        );
      case "copywriting/article/write-post-seo":
        return defineAsyncComponent(() => import("./TaskResultWriteSeoArticle.vue"));
      case "social-media/linkedin/craft-job-postings":
        return defineAsyncComponent(() => import("./TaskResultLinkedin.vue"));
      case "paid-advertising/ads/write-ad-variations":
        return defineAsyncComponent(() => import("./TaskResultAdVariations.vue"));
      case "paid-advertising/ads/write-search-ad-copy":
        return defineAsyncComponent(() => import("./TaskResultGoogleSearchAd.vue"));
    }
  }

  // Then check output type for generic handling
  if (output && ["string", "list", "listToArray"].includes(output.type))
    return defaultComponent;
  else if (
    output &&
    output.type === "json" &&
    output?.schema &&
    (output.schema["ui:template"] || output.schema["ma-custom:ui:template"])
  ) {
    switch (output.schema["ui:template"] || output.schema["ma-custom:ui:template"]) {
      case "custom-identify-keyword":
        return defineAsyncComponent(
          () => import("./subTasks/MarketArticleIdentifyKeywords.vue")
        );
      case "core-facebook-post-wrapper":
        return defineAsyncComponent(() => import("./TaskResultFacebook.vue"));
      case "core-linkedin-post-wrapper":
        return defineAsyncComponent(() => import("./TaskResultLinkedin.vue"));
      case "core-twitter-post-wrapper":
        return defineAsyncComponent(() => import("./TaskResultTwitter.vue"));
      case "core-instagram-post-wrapper":
        return defineAsyncComponent(() => import("./TaskResultInstagram.vue"));
      case "core-tiktok-post-wrapper":
        return defineAsyncComponent(() => import("./TaskResultTikTok.vue"));
      case "core-youtube-video-description-wrapper":
      case "core-youtube-community-post-wrapper":
      case "core-youtube-pinned-comment-wrapper":
      case "core-youtube-end-screen-cta-wrapper":
      case "core-youtube-channel-description-wrapper":
        return defineAsyncComponent(() => import("./TaskResultYouTube.vue"));
      default:
        return defaultComponent;
    }
  }

  return defaultComponent;
});
</script>
